import React, { memo, useCallback, useMemo } from "react";

import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import { getDistributionGroupMention } from "modules/utils/Functions";
import colors from "app/colors";
import { CircleMinusIcon, WarningIcon } from "modules/icons";
import Toast from "modules/toasts/toast";
import {
  AdminDistributionGroupsSetActiveMutation,
  AdminDistributionGroupsSetActiveMutationVariables,
} from "gql/graphql";
import ADMIN_DISTRIBUTION_GROUPS_SET_ACTIVE_MUTATION from "./_networking/admin-distribution-groups-set-active";
import useDisplayToast from "modules/hooks/use-display-toast";
import { useOverlay } from "modules/providers/overlay-provider";

interface IProps {
  distributionGroupIds: string[];
}

const AdminConfirmArchiveDistriubtionGroupModal = ({
  distributionGroupIds,
}: IProps) => {
  const { closeModal } = useOverlay();
  const { distributionGroups: data } = useDistributionGroups();
  const { displayToast } = useDisplayToast();
  const [adminDistributionGroupsSetActiveApi] = useMutation<
    AdminDistributionGroupsSetActiveMutation,
    AdminDistributionGroupsSetActiveMutationVariables
  >(ADMIN_DISTRIBUTION_GROUPS_SET_ACTIVE_MUTATION);

  const distributionGroups = useMemo(
    () =>
      data?.filter((distributionGroup) =>
        distributionGroupIds.includes(distributionGroup.id),
      ),
    [data, distributionGroupIds],
  );

  const distributionGroupsFirstThree = distributionGroups.slice(0, 3);

  const onArchiveClicked = useCallback(() => {
    adminDistributionGroupsSetActiveApi({
      variables: {
        active: false,
        idList: distributionGroupIds,
      },
    });
    displayToast(() => (
      <Toast
        message={`${distributionGroupIds.length} Groups Archived`}
        leftIcon={<CircleMinusIcon boxSize={5} fill={colors.grayMain} />}
      />
    ));
    closeModal();
  }, [
    adminDistributionGroupsSetActiveApi,
    closeModal,
    distributionGroupIds,
    displayToast,
  ]);

  return (
    <Modal isOpen onClose={closeModal} size="xs" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader pt={8}>
          <VStack spacing={6}>
            <WarningIcon />
            <Text fontSize={26}>
              Archive Group{distributionGroups.length > 1 && "s"}?
            </Text>
          </VStack>
        </ModalHeader>
        <ModalBody pb={6}>
          <VStack spacing={6}>
            <Text fontSize={16} color={colors.gray50} textAlign="center">
              Are you sure you want to archive:
              <Text>
                {distributionGroupsFirstThree.map(
                  (distributionGroup, index) => (
                    <React.Fragment key={distributionGroup.id}>
                      {index < 3 && index > 0 && ", "}
                      {getDistributionGroupMention(distributionGroup)}
                    </React.Fragment>
                  ),
                )}
                {distributionGroups.length > 3
                  ? `, and ${distributionGroups.length - 3} more?`
                  : "?"}
              </Text>
            </Text>
            <Text fontSize={12} color={colors.gray50} textAlign="center">
              Note: Conversations with archived groups will still be visible.
              However, archived groups cannot be used as callouts in new
              conversations.
            </Text>
            <HStack>
              <Button variant="cancel" height={8} w={28} onClick={closeModal}>
                No
              </Button>
              <Button height={8} w={28} onClick={onArchiveClicked}>
                Archive
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(AdminConfirmArchiveDistriubtionGroupModal);
